import { collection, getDocs, updateDoc, doc, writeBatch, addDoc, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase/config';

export const normalizeDisplayName = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
};

export const migrateUserDisplayNames = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  
  const updates = snapshot.docs.map(async (userDoc) => {
    const userData = userDoc.data();
    if (userData.displayName && !userData.normalizedDisplayName) {
      const normalizedName = normalizeDisplayName(userData.displayName);
      await updateDoc(doc(db, 'users', userDoc.id), {
        normalizedDisplayName: normalizedName
      });
      console.log(`Updated user ${userData.displayName} with normalized name ${normalizedName}`);
    }
  });
  
  await Promise.all(updates);
  console.log('Display name migration completed');
};

export const migrateUsersToPublic = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  
  // Use batched writes for better performance and atomicity
  const batch = writeBatch(db);
  let count = 0;

  snapshot.docs.forEach((userDoc) => {
    const userData = userDoc.data();
    // Only update if isPublic is not already true
    if (userData.isPublic !== true) {
      batch.update(doc(db, 'users', userDoc.id), {
        isPublic: true,
        preferences: {
          ...userData.preferences,
          isPublic: true
        }
      });
      count++;
    }
  });

  if (count > 0) {
    await batch.commit();
    console.log(`Updated ${count} users to be public`);
  } else {
    console.log('No users needed to be updated');
  }
  
  return count;
};

export const migrateSolvesToTopLevel = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  let totalSolves = 0;

  for (const userDoc of snapshot.docs) {
    const userData = userDoc.data();
    if (userData.solves && Array.isArray(userData.solves)) {
      // Add each solve to the top-level solves collection
      for (const solve of userData.solves) {
        await addDoc(collection(db, 'solves'), {
          ...solve,
          userId: userDoc.id,
          createdAt: solve.timestamp || new Date(),
        });
        totalSolves++;
      }

      // Clear solves array from user document
      await updateDoc(doc(db, 'users', userDoc.id), {
        solves: []
      });
    }
  }

  console.log(`Migrated ${totalSolves} solves to top-level collection`);
  return totalSolves;
};

export const fixEmptyNormalizedDisplayNames = async () => {
  const usersRef = collection(db, 'users');
  const snapshot = await getDocs(usersRef);
  
  const updates = snapshot.docs.map(async (userDoc) => {
    const userData = userDoc.data();
    if (userData.displayName && (!userData.normalizedDisplayName || userData.normalizedDisplayName === '')) {
      const normalizedName = normalizeDisplayName(userData.displayName);
      await updateDoc(doc(db, 'users', userDoc.id), {
        normalizedDisplayName: normalizedName
      });
      console.log(`Updated user ${userData.displayName} with normalized name ${normalizedName}`);
    }
  });
  
  await Promise.all(updates);
  console.log('Empty normalized display name migration completed');
};

export const migrateOrphanedSolves = async () => {
  const solvesRef = collection(db, 'solves');
  const sessionsRef = collection(db, 'sessions');
  const batch = writeBatch(db);
  let totalMigrated = 0;

  // Get all solves that don't have a sessionId
  const q = query(
    solvesRef,
    where('sessionId', '==', null)
  );
  const snapshot = await getDocs(q);

  // Group solves by userId and puzzleType
  const solvesByUserAndType: Record<string, Record<string, any[]>> = {};
  snapshot.docs.forEach(doc => {
    const solve = doc.data();
    const userId = solve.userId;
    const puzzleType = solve.puzzleType || '333'; // Default to 3x3 if not specified
    
    if (!solvesByUserAndType[userId]) {
      solvesByUserAndType[userId] = {};
    }
    if (!solvesByUserAndType[userId][puzzleType]) {
      solvesByUserAndType[userId][puzzleType] = [];
    }
    solvesByUserAndType[userId][puzzleType].push({
      id: doc.id,
      ...solve
    });
  });

  // Create legacy sessions for each user and puzzle type
  for (const userId in solvesByUserAndType) {
    for (const puzzleType in solvesByUserAndType[userId]) {
      const solves = solvesByUserAndType[userId][puzzleType];
      if (solves.length === 0) continue;

      // Create a new legacy session
      const sessionRef = doc(sessionsRef);
      const sessionData = {
        userId,
        puzzleType,
        startTime: Math.min(...solves.map(s => s.timestamp)),
        isTrainingSession: false,
        name: 'Legacy Session',
        solveCount: solves.length
      };

      batch.set(sessionRef, sessionData);

      // Update all solves with the new sessionId
      solves.forEach(solve => {
        const solveRef = doc(solvesRef, solve.id);
        batch.update(solveRef, { sessionId: sessionRef.id });
      });

      totalMigrated += solves.length;
    }
  }

  if (totalMigrated > 0) {
    await batch.commit();
  }

  console.log(`Migrated ${totalMigrated} orphaned solves to legacy sessions`);
  return totalMigrated;
}; 